import { httpClient } from '../funcs'
import { useLocalStorage } from './useLocalStorage'
import {
    isTokenExpired,
    refreshAccessToken,
    removeOAuth2Tokens,
    isOAuth2User as isOAuth2UserFromTokens,
    getOAuth2TokensFromStorage,
    isOAuth2AccessJwt,
    isPersistRefreshSession,
    oauth2LocalStorageValue,
    setMemoryAccessToken,
    getMemoryAccessToken,
    OAuth2Tokens,
    SaveOAuth2Options,
} from './oauth2Tokens'
import {
    isRefreshInFlight,
    setRefreshInFlight,
    trackAuthSessionEvent,
    trackSessionCleared,
    SessionClearReason,
} from './authSessionTelemetry'
import {
    accessIssuedAt,
    installAuthTabSync,
    publishAccessToken,
    requestPeerAccessToken,
} from './authTabSync'

// Хук для работы с access_token в localStorage (для обратной совместимости)
const [get, set, remove] = useLocalStorage("access_token")

/** Один общий refresh: параллельные 401 ждут тот же Promise. */
let refreshPromise: Promise<boolean> | null = null

/**
 * Сохраняет старый токен (не OAuth2)
 * @param access_token - access token для сохранения
 */
export function saveLegacyToken(access_token: string): void {
    if (isOAuth2AccessJwt(access_token)) {
        return
    }
    // Очищаем OAuth2 токены, иначе getToken() будет возвращать старый токен из "token"
    removeOAuth2Tokens()

    // Устанавливаем access_token в httpClient
    httpClient.defaults.headers['Authorization'] = `Bearer ${access_token}`
    
    // Сохраняем в localStorage по старинке
    set(access_token)
}

/**
 * Сохраняет OAuth2 токены.
 * Access только в памяти вкладки. Кабинет: в LS флаг `{}`, refresh — httpOnly cookie.
 * persistRefresh: партнёр и саппорт-URL — refresh в LS, access всё равно в памяти.
 */
export function saveOAuth2Token(oauth2Tokens: OAuth2Tokens, options?: SaveOAuth2Options): void {
    httpClient.defaults.headers['Authorization'] = `Bearer ${oauth2Tokens.access_token}`
    remove()
    setMemoryAccessToken(oauth2Tokens.access_token)
    publishAccessToken(oauth2Tokens.access_token)
    localStorage.setItem(
        'token',
        oauth2LocalStorageValue(oauth2Tokens, options?.persistRefresh === true),
    )
}

/** Логин/смена аккаунта кабинета: ставим cookie, затем убираем refresh из LS. */
export async function saveCabinetOAuth2Session(oauth2Tokens: OAuth2Tokens): Promise<void> {
    if (!oauth2Tokens.refresh_token) {
        saveOAuth2Token(oauth2Tokens)
        return
    }
    saveOAuth2Token(oauth2Tokens, { persistRefresh: true })
    try {
        const { data } = await httpClient.post(
            '/user/refresh_token',
            { refresh_token: oauth2Tokens.refresh_token },
            { skipAuthRefresh: true, withCredentials: true } as any,
        )
        saveOAuth2Token({
            access_token: data?.access_token || oauth2Tokens.access_token,
            refresh_token: '',
        })
    } catch (error) {
        console.warn('Failed to seed refresh cookie, keeping refresh in localStorage', error)
    }
}

/**
 * Сохраняет токен (автоматически определяет тип)
 * @param access_token - access token для сохранения
 * @param oauth2Tokens - OAuth2 токены (опционально)
 */
export function saveToken(access_token: string, oauth2Tokens?: OAuth2Tokens): void {
    if (oauth2Tokens) {
        saveOAuth2Token(oauth2Tokens)
    } else {
        saveLegacyToken(access_token)
    }
}

/** Синхронизация хранилища после GET /user/self (см. fetch_self_info: oauth2 без лишнего access_token). */
export function persistTokensFromUserSelfResponse(user: any): void {
    const hasOAuth2Tokens = user.tokens && Array.isArray(user.tokens) && user.tokens.length > 0

    const stored = getOAuth2TokensFromStorage()
    const keepLsRefresh = isPersistRefreshSession()

    if (hasOAuth2Tokens) {
        saveOAuth2Token({
            access_token: user.tokens[0].access_token,
            refresh_token: keepLsRefresh ? stored!.refresh_token : '',
        }, { persistRefresh: keepLsRefresh })
        return
    }

    if (keepLsRefresh) {
        if (user.access_token) {
            saveOAuth2Token({
                access_token: user.access_token,
                refresh_token: stored!.refresh_token,
            }, { persistRefresh: true })
        }
        return
    }

    if (user.access_token) {
        if (isOAuth2AccessJwt(user.access_token)) {
            return
        }
        saveLegacyToken(user.access_token)
    }
}

/**
 * Access: OAuth2 из памяти вкладки, легаси — из localStorage.
 */
export function getToken(): string | null {
    const oauth2Tokens = getOAuth2TokensFromStorage()
    if (oauth2Tokens) {
        return oauth2Tokens.access_token || null
    }

    const legacy = get()
    if (legacy == null || legacy === '') {
        return null
    }
    const legacyStr = typeof legacy === 'string' ? legacy : String(legacy)
    if (isOAuth2AccessJwt(legacyStr)) {
        remove()
        return null
    }

    return legacyStr
}

/**
 * Поднимает access из памяти / старого LS и ставит Authorization.
 */
export function loadToken(): void {
    const token = getToken()
    if (token) {
        httpClient.defaults.headers['Authorization'] = `Bearer ${token}`
    }
}

/**
 * Удаляет все токены
 */
export function removeToken(
    reason: SessionClearReason = 'unknown',
    extra: Record<string, string | number | boolean | null | undefined> = {},
): void {
    trackSessionCleared(reason, extra)

    // Удаляем старый access_token
    remove()
    
    // Удаляем OAuth2 токены
    removeOAuth2Tokens()
    
    // Удаляем Authorization заголовок
    delete httpClient.defaults.headers['Authorization']

    void clearRefreshCookie()
}

/** httpOnly cookie JS не читает — снимаем запросом на API. */
async function clearRefreshCookie(): Promise<void> {
    try {
        await httpClient.post(
            '/user/clear_refresh_cookie',
            {},
            { skipAuthRefresh: true, withCredentials: true } as any,
        )
    } catch {
        // cookie могло не быть
    }
}

/**
 * Проверяет, использует ли пользователь OAuth2
 */
export function isOAuth2User(): boolean {
    return isOAuth2UserFromTokens()
}

export { isRefreshInFlight }

/** Access из другой вкладки того же браузера. Свой токен не трогаем. */
export function adoptPeerAccessToken(token: string): void {
    if (!token || token === getMemoryAccessToken()) {
        return
    }
    const current = getMemoryAccessToken()
    if (current && accessIssuedAt(token) < accessIssuedAt(current)) {
        return
    }
    setMemoryAccessToken(token)
    httpClient.defaults.headers['Authorization'] = `Bearer ${token}`
}

/**
 * Проверяет валидность токена и обновляет его при необходимости.
 * Параллельные вызовы разделяют один refresh (single-flight).
 */
export async function ensureValidToken(
    props?: any,
    options?: { force?: boolean },
): Promise<boolean> {
    // Если пользователь не использует OAuth2, токены не истекают
    if (!isOAuth2User()) {
        return true
    }

    // Новая вкладка без access в памяти: берём его у уже открытой, не обновляя сессию.
    if (!getMemoryAccessToken()) {
        const peer = await requestPeerAccessToken()
        if (peer) {
            adoptPeerAccessToken(peer)
        }
    }

    // 401 при живом JWT: сервер уже не принимает этот access (его сменила другая сессия).
    const force = options?.force === true
    if (!force && !isTokenExpired()) {
        return true
    }

    if (refreshPromise) {
        trackAuthSessionEvent('token_refresh_await_inflight')
        return refreshPromise
    }

    refreshPromise = (async () => {
        setRefreshInFlight(true)
        trackAuthSessionEvent('token_refresh_start', { source: 'ensureValidToken' })
        try {
            // logout при провале refresh делает вызывающая сторона (interceptor / loadSelf)
            const newTokens = await refreshAccessToken({ skipSessionClear: true })
            const ok = Boolean(newTokens)
            trackAuthSessionEvent(ok ? 'token_refresh_ok' : 'token_refresh_fail', {
                source: 'ensureValidToken',
            })
            return ok
        } catch (error) {
            trackAuthSessionEvent('token_refresh_fail', {
                source: 'ensureValidToken',
                error: String((error as any)?.message || error),
            })
            return false
        } finally {
            setRefreshInFlight(false)
            refreshPromise = null
        }
    })()

    return refreshPromise
}

installAuthTabSync(
    () => getMemoryAccessToken(),
    (token) => adoptPeerAccessToken(token),
)
